import React, { useCallback } from 'react';
import { ModalBody, ModalFooterButtons } from '@televet/kibble-ui/build/components/Modal';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { useCreateMassTextAlertMutation, MassTextAlertStatus, NumberOfPetParentsReached } from 'shared/types/graphql';
import { SelectedDateRange, DateStringObject } from '..';
import useClinicUser from 'shared/hooks/useClinicUser';
import { formatPluralization } from 'shared/utils';

interface ModalProps {
  messageText: string;
  selectedDateRange: SelectedDateRange | null;
  refetchActivityLogData: () => void;
  setMessageText: (message: string) => void;
  setRemainingCharCount: React.Dispatch<React.SetStateAction<number>>;
  formatDateString: (dateString: Date | undefined) => DateStringObject | undefined;
  setSelectedDateRange: (selectedDateRange: SelectedDateRange | null) => void;
  estimatedSendTimeData:
    | Pick<NumberOfPetParentsReached, 'numberOfPetPerents' | 'timeEstimateInSeconds'>
    | null
    | undefined;
  formatTimeEstimate: () => string | undefined;
  onCloseModal: () => void;
}
const MassTextAlertsSendConfirmationModal = ({
  refetchActivityLogData,
  messageText,
  setMessageText,
  setRemainingCharCount,
  selectedDateRange,
  setSelectedDateRange,
  estimatedSendTimeData,
  formatTimeEstimate,
  onCloseModal,
  formatDateString,
}: ModalProps): JSX.Element => {
  const { clinicUser, currentClinicId } = useClinicUser();
  const toast = useToast({ variant: 'solid' });
  const [sendMassTextAlert, { loading: sendingMassTextAlert }] = useCreateMassTextAlertMutation();

  const onConfirmButtonClick = useCallback(async () => {
    if (messageText && selectedDateRange?.optionName) {
      const isoFomattedStartDate = formatDateString(selectedDateRange?.startDate)?.isoString;
      const isoFormattedEndDate = formatDateString(selectedDateRange?.endDate)?.isoString;

      try {
        await sendMassTextAlert({
          variables: {
            data: {
              clinic: {
                connect: {
                  id: currentClinicId,
                },
              },
              alertBody: messageText,
              appointmentDateTimeFrom: isoFomattedStartDate,
              appointmentDateTimeTo: isoFormattedEndDate,
              appointmentDateRangeType: selectedDateRange?.optionName,
              status: MassTextAlertStatus.Processing,
              createdByUser: {
                connect: {
                  id: clinicUser?.id,
                },
              },
            },
          },
        });
        setSelectedDateRange(null);
        setMessageText('');
        setRemainingCharCount(450);
        refetchActivityLogData();
        toast({
          description: 'Alert has been sent',
          position: 'top',
          variant: 'subtle',
          status: 'success',
        });
        onCloseModal();
        localStorage.removeItem('massTextDraftMessage');
      } catch (e) {
        toast({
          description: 'Error sending alert',
          position: 'top',
          variant: 'subtle',
          status: 'error',
        });
        console.log(e);
        return;
      }
    }
  }, [
    messageText,
    selectedDateRange?.optionName,
    selectedDateRange?.startDate,
    selectedDateRange?.endDate,
    formatDateString,
    sendMassTextAlert,
    currentClinicId,
    clinicUser?.id,
    setSelectedDateRange,
    setMessageText,
    setRemainingCharCount,
    refetchActivityLogData,
    toast,
    onCloseModal,
  ]);

  return (
    <>
      <ModalBody>
        <Text mt="5" size="md">
          This message will be sent to {estimatedSendTimeData?.numberOfPetPerents}{' '}
          {formatPluralization({
            word: 'client',
            needsArticle: false,
            singularCriteria: estimatedSendTimeData?.numberOfPetPerents || 0,
          })}
          , and will take {formatTimeEstimate()?.toLowerCase()} to send.
        </Text>
      </ModalBody>
      <ModalFooterButtons
        mixpanelName="Send mass text alert button"
        onSubmit={onConfirmButtonClick}
        isLoading={sendingMassTextAlert}
        submitText="Confirm Send"
      />
    </>
  );
};

export default MassTextAlertsSendConfirmationModal;
